import React, { ChangeEvent, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";

type RowData = Record<string, unknown>;

const pageWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "20px",
};

const headerRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  flexWrap: "wrap",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "34px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.03em",
};

const subtitleStyle: React.CSSProperties = {
  margin: "8px 0 0 0",
  color: "var(--text-secondary)",
  fontSize: "15px",
  lineHeight: 1.6,
};

const actionsWrapStyle: React.CSSProperties = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
};

const kpiGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "16px",
};

const kpiLabelStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
  marginBottom: "10px",
};

const kpiValueStyle: React.CSSProperties = {
  fontSize: "28px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.03em",
};

const kpiMetaStyle: React.CSSProperties = {
  marginTop: "10px",
  fontSize: "13px",
  color: "var(--text-secondary)",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "18px",
  fontWeight: 700,
  color: "var(--text-primary)",
};

const sectionTextStyle: React.CSSProperties = {
  margin: "6px 0 0 0",
  color: "var(--text-secondary)",
  fontSize: "14px",
};

const tableWrapStyle: React.CSSProperties = {
  overflowX: "auto",
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.02)",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "14px 12px",
  color: "var(--text-secondary)",
  fontSize: "13px",
  fontWeight: 700,
  borderBottom: "1px solid var(--border)",
  background: "rgba(255,255,255,0.02)",
};

const tdStyle: React.CSSProperties = {
  padding: "14px 12px",
  color: "var(--text-primary)",
  fontSize: "14px",
  borderBottom: "1px solid var(--border)",
  verticalAlign: "middle",
};

const dropZoneStyle: React.CSSProperties = {
  border: "1px dashed var(--border)",
  borderRadius: "20px",
  background: "rgba(255,255,255,0.02)",
  padding: "24px",
};

const ManualLoad = () => {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState<RowData[]>([]);
  const [filterText, setFilterText] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [sending, setSending] = useState<boolean>(false);

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);

    const reader = new FileReader();

    reader.onload = (evt) => {
      const bstr = evt.target?.result;
      if (typeof bstr !== "string") return;

      const wb = XLSX.read(bstr, { type: "binary", cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rawData = XLSX.utils.sheet_to_json<RowData>(ws, { defval: "" });

      const dataConFechas = rawData.map((row) => {
        const nuevaFila: RowData = { ...row };

        ["Fecha Colecta", "Fecha Venta"].forEach((col) => {
          const valor = row[col];

          if (typeof valor === "number") {
            const excelEpoch = new Date(1900, 0, valor - 1);
            nuevaFila[col] = excelEpoch.toISOString().slice(0, 10);
          }
        });

        return nuevaFila;
      });

      setData(dataConFechas);
    };

    reader.readAsBinaryString(file);
  };

const enviarDatos = async () => {
  if (!token) {
    alert("❌ Sesión inválida. Volvé a iniciar sesión.");
    return;
  }

  try {
    setSending(true);

    const res = await authFetch("/v1/utils/api/envios", {
      method: "POST",
      token,
      json: true,
      body: JSON.stringify(data),
    });

    const payload = await res.json().catch(() => ({}));

    if (!res.ok) {
      if (res.status === 401) {
        throw new Error("Sesión inválida o vencida. Volvé a iniciar sesión.");
      }

      if (res.status === 403) {
        throw new Error(
          "Tu usuario no tiene permiso para realizar cargas manuales. Revisá el permiso db_access del administrador."
        );
      }

      throw new Error(payload?.error || payload?.message || "Error al guardar");
    }

    alert("✅ Datos guardados en PostgreSQL");
  } catch (err) {
    console.error(err);
    alert(
      `❌ ${
        err instanceof Error
          ? err.message
          : "Error guardando datos en PostgreSQL"
      }`
    );
  } finally {
    setSending(false);
  }
};
  const filteredData = useMemo(() => {
    const query = filterText.toLowerCase();

    return data.filter((row) =>
      Object.values(row).some((cell) =>
        String(cell).toLowerCase().includes(query)
      )
    );
  }, [data, filterText]);

  const columns = useMemo(() => {
    if (!data.length) return [];
    return Object.keys(data[0]);
  }, [data]);

  const totalRows = data.length;
  const visibleRows = filteredData.length;
  const totalColumns = columns.length;
  const hasData = data.length > 0;

  return (
    <div style={pageWrapStyle}>
      <div style={headerRowStyle}>
        <div>
          <h1 style={titleStyle}>Carga manual</h1>
          <p style={subtitleStyle}>
            Subí un Excel, revisá la información y enviá los datos para alimentar
            el flujo de liquidaciones y totales.
          </p>
        </div>

        <div style={actionsWrapStyle}>
          <Button
            variant="secondary"
            onClick={() => navigate("/admin/liquidaciones/totales")}
          >
            Ir a Totales
          </Button>

          <Button onClick={enviarDatos} disabled={!hasData || sending}>
            {sending ? "Enviando..." : "Enviar a PostgreSQL"}
          </Button>
        </div>
      </div>

      <div style={kpiGridStyle} className="manual-load-kpi-grid">
        <Card>
          <div style={kpiLabelStyle}>Archivo</div>
          <div style={{ ...kpiValueStyle, fontSize: "20px" }}>
            {fileName || "Sin archivo"}
          </div>
          <div style={kpiMetaStyle}>Último archivo cargado en sesión.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Filas cargadas</div>
          <div style={kpiValueStyle}>{totalRows}</div>
          <div style={kpiMetaStyle}>Cantidad total de registros leídos.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Filas visibles</div>
          <div style={kpiValueStyle}>{visibleRows}</div>
          <div style={kpiMetaStyle}>Resultados actuales según filtro.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Columnas</div>
          <div style={kpiValueStyle}>{totalColumns}</div>
          <div style={kpiMetaStyle}>Cantidad de campos detectados.</div>
        </Card>
      </div>

      <Card>
        <div style={{ marginBottom: "14px" }}>
          <h2 style={sectionTitleStyle}>Carga de archivo</h2>
          <p style={sectionTextStyle}>
            Seleccioná un archivo Excel de LightData u otra exportación compatible.
          </p>
        </div>

        <div style={dropZoneStyle}>
          <label
            htmlFor="manual-load-file"
            style={{
              display: "block",
              marginBottom: "10px",
              fontSize: "12px",
              color: "var(--text-secondary)",
            }}
          >
            Archivo Excel
          </label>

          <input
            id="manual-load-file"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            style={{
              width: "100%",
              color: "var(--text-primary)",
            }}
          />

          <p
            style={{
              margin: "10px 0 0 0",
              fontSize: "13px",
              color: "var(--text-secondary)",
            }}
          >
            Formatos aceptados: .xlsx y .xls
          </p>
        </div>
      </Card>

      <Card>
        <div style={{ marginBottom: "14px" }}>
          <h2 style={sectionTitleStyle}>Vista previa</h2>
          <p style={sectionTextStyle}>
            Filtrá registros por chofer, tienda, tracking o cualquier dato
            contenido en la tabla.
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: "16px",
            marginBottom: "18px",
          }}
          className="manual-load-filters-grid"
        >
          <div>
            <Input
              type="text"
              placeholder="Filtrar por chofer, cliente, tracking, dirección, etc."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
          </div>

          <div style={{ display: "flex", alignItems: "stretch" }}>
            <Button
              variant="secondary"
              onClick={() => setFilterText("")}
              disabled={!filterText}
            >
              Limpiar
            </Button>
          </div>
        </div>

        {!hasData ? (
          <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
            Todavía no hay datos cargados. Subí un Excel para ver la vista previa.
          </div>
        ) : (
          <div style={tableWrapStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  {columns.map((key) => (
                    <th key={key} style={thStyle}>
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {filteredData.map((row, i) => (
                  <tr key={i}>
                    {columns.map((key) => (
                      <td key={`${i}-${key}`} style={tdStyle}>
                        {String(row[key] ?? "")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <style>
        {`
          .manual-load-kpi-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }

          .manual-load-filters-grid {
            grid-template-columns: 1fr auto;
          }

          @media (max-width: 1180px) {
            .manual-load-kpi-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
          }

          @media (max-width: 720px) {
            .manual-load-kpi-grid,
            .manual-load-filters-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ManualLoad;